import {useMemo, useState, useSyncExternalStore} from 'react';
import {CartAccessory, CartSheet, CategoryRail, MobileOrderingShell, MutationFeedback, OrderingHeader, ProductList, UiButton, UiCheckbox, UiRadioGroup} from '@morefunos/ui';
import type {ProductOptionSelectionInput} from '../../../packages/order/product-selection.ts';
import type {CustomerComboConfiguration, CustomerComboPort, CustomerPaymentSubmission} from './customer-combo-port.ts';
import {uploadCustomerPaymentProof, type CustomerPaymentProofUploadPort} from './customer-payment-proof-upload.ts';

function ConfigForm({config, port}: {readonly config: CustomerComboConfiguration; readonly port: CustomerComboPort}) {
  const [selected, setSelected] = useState<Record<string, readonly string[]>>({});
  const valid = config.groups.every((group) => {
    const count = selected[group.optionGroupId]?.length ?? 0;
    return count >= group.min && count <= group.max;
  });
  const submit = () => {
    if (!valid) return;
    const selections: ProductOptionSelectionInput[] = config.groups.flatMap((group) => {
      const optionIds = selected[group.optionGroupId] ?? [];
      return optionIds.length ? [{optionGroupId: group.optionGroupId, optionIds}] : [];
    });
    port.configuredAdd(config.productId, selections);
  };
  return <div className="customer-config">
    {config.groups.map((group) => group.max === 1 ? (
      <UiRadioGroup key={group.optionGroupId} label={group.label} value={selected[group.optionGroupId]?.[0] ?? null}
        onChange={(value) => setSelected((current) => ({...current, [group.optionGroupId]: [value]}))}
        items={group.options.map((option) => ({value: option.optionId, label: option.priceDeltaLabel ? `${option.label} ${option.priceDeltaLabel}` : option.label, isDisabled: !option.enabled}))} />
    ) : <fieldset key={group.optionGroupId}><legend>{group.label}</legend>{group.options.map((option) => <UiCheckbox key={option.optionId} label={option.label}
      isSelected={(selected[group.optionGroupId] ?? []).includes(option.optionId)} isDisabled={!option.enabled}
      onChange={() => setSelected((current) => {const values = current[group.optionGroupId] ?? []; const next = values.includes(option.optionId) ? values.filter((id) => id !== option.optionId) : values.length < group.max ? [...values, option.optionId] : values; return {...current, [group.optionGroupId]: next};})} />)}</fieldset>)}
    <UiButton variant="primary" isDisabled={!valid} onPress={submit}>加入購物車</UiButton>
  </div>;
}

export interface CustomerAppProps {
  readonly port: CustomerComboPort | null;
  readonly paymentProofUpload?: CustomerPaymentProofUploadPort;
  readonly businessDate?: () => string;
}

type CustomerProofUiStatus = 'pending' | 'resubmission_required' | 'approved' | 'unknown';

const customerProofStatusLabel = (status: CustomerProofUiStatus): string => {
  switch (status) {
    case 'pending': return '等待店員核對';
    case 'resubmission_required': return '需要重新提交';
    case 'approved': return '付款證明已核對';
    default: return '狀態暫未同步';
  }
};

export function App({port, paymentProofUpload, businessDate}: CustomerAppProps) {
  if (!port) return <main className="customer-app customer-blocked"><MutationFeedback tone="critical" message="暫時未能點餐" detail="未連接 MoreFunOS Runtime，唔會顯示假餐牌或假價錢。" /></main>;
  return <Connected port={port} paymentProofUpload={paymentProofUpload} businessDate={businessDate} />;
}

function Connected({port, paymentProofUpload, businessDate}: {
  readonly port: CustomerComboPort;
  readonly paymentProofUpload?: CustomerPaymentProofUploadPort;
  readonly businessDate?: () => string;
}) {
  const snapshot = useSyncExternalStore(port.subscribe, port.getSnapshot, port.getSnapshot);
  const [category, setCategory] = useState<string | undefined>();
  const [feedback, setFeedback] = useState('選好餐點後到購物車確認');
  const [paymentMode, setPaymentMode] = useState<CustomerPaymentSubmission['mode']>('pay-at-store');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastElectronicSubmissionId, setLastElectronicSubmissionId] = useState<string | null>(null);
  const [proofStatus, setProofStatus] = useState<CustomerProofUiStatus>('unknown');
  const [resubmitFile, setResubmitFile] = useState<File | null>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const active = snapshot.categories.some((item) => item.id === category) ? category : snapshot.categories[0]?.id;
  const products = useMemo(() => active ? snapshot.products.filter((item) => item.categoryId === active) : snapshot.products, [active, snapshot.products]);
  const productItems = products.map((item) => ({
    id: item.id, categoryId: item.categoryId, name: item.name, priceLabel: item.priceLabel, ...(item.subtitle ? {subtitle: item.subtitle} : {}),
    action: item.action === 'direct' ? {kind: 'direct' as const, label: '＋'} : item.action === 'configure' ? {kind: 'configure' as const, label: '選擇'} : {kind: 'disabled' as const, label: '暫不可選', disabledReason: '目前不可供應'},
  }));
  const cartItems = snapshot.lines.map((line) => ({id: line.id, name: line.name, quantityLabel: String(line.quantity), canDecrease: true, canIncrease: true, canRemove: true}));

  const selectPaymentMode = (value: string) => {
    if (value !== 'pay-at-store' && value !== 'electronic-proof') return;
    if (value === 'electronic-proof' && !paymentProofUpload) return;
    setPaymentMode(value);
    if (value === 'pay-at-store') setProofFile(null);
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      let payment: CustomerPaymentSubmission;
      if (paymentMode === 'electronic-proof') {
        if (!paymentProofUpload) throw new Error('CUSTOMER_PAYMENT_PROOF_UPLOAD_UNAVAILABLE');
        if (!businessDate) throw new Error('CUSTOMER_BUSINESS_DATE_REQUIRED');
        if (!proofFile) throw new Error('CUSTOMER_PAYMENT_PROOF_FILE_REQUIRED');
        setFeedback('正在上載付款證明…');
        const evidenceRef = await uploadCustomerPaymentProof(paymentProofUpload, {
          file: proofFile,
          businessDate: businessDate(),
          uploadedAt: new Date().toISOString(),
        });
        payment = {mode: 'electronic-proof', evidenceRef};
      } else {
        payment = {mode: 'pay-at-store'};
      }
      const result = port.submit(payment);
      setProofFile(null);
      setFeedback(`訂單已送出：${result.submissionId}`);
      if (payment.mode === 'electronic-proof') {
        setLastElectronicSubmissionId(result.submissionId);
        setProofStatus('pending');
        setResubmitFile(null);
      }
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : '落單失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const refreshProofStatus = () => {
    if (!lastElectronicSubmissionId) return;
    const proof = port.paymentProofStatus(lastElectronicSubmissionId);
    const status = proof?.status;
    if (status === 'pending' || status === 'resubmission_required' || status === 'approved') {
      setProofStatus(status);
    } else {
      setProofStatus('unknown');
    }
  };

  const resubmitPaymentProof = async () => {
    if (!lastElectronicSubmissionId || resubmitting) return;
    setResubmitting(true);
    try {
      if (!paymentProofUpload) throw new Error('CUSTOMER_PAYMENT_PROOF_UPLOAD_UNAVAILABLE');
      if (!businessDate) throw new Error('CUSTOMER_BUSINESS_DATE_REQUIRED');
      if (!resubmitFile) throw new Error('CUSTOMER_PAYMENT_PROOF_FILE_REQUIRED');
      setFeedback('正在重新上載付款證明…');
      const evidenceRef = await uploadCustomerPaymentProof(paymentProofUpload, {
        file: resubmitFile,
        businessDate: businessDate(),
        uploadedAt: new Date().toISOString(),
      });
      port.resubmitPaymentProof(lastElectronicSubmissionId, evidenceRef);
      setResubmitFile(null);
      setProofStatus('pending');
      setFeedback('付款證明已重新提交，等待店員核對');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : '重新提交失敗');
    } finally {
      setResubmitting(false);
    }
  };

  const electronicReady = Boolean(paymentProofUpload && proofFile);
  const checkoutDisabled = snapshot.quoteStatus !== 'quoted' || submitting ||
    (paymentMode === 'electronic-proof' && !electronicReady);
  const primaryActionLabel = submitting
    ? '處理中…'
    : paymentMode === 'electronic-proof'
      ? '上載證明並送出'
      : '送出自取訂單';

  const checkoutFooter = <div className="customer-checkout-footer">
    <section className="customer-payment" aria-label="付款方式">
      <UiRadioGroup
        label="付款方式"
        value={paymentMode}
        onChange={selectPaymentMode}
        items={[
          {value: 'pay-at-store', label: '到店付款'},
          {value: 'electronic-proof', label: '電子付款（上載截圖）', isDisabled: !paymentProofUpload},
        ]}
      />
      {!paymentProofUpload ? <p className="customer-payment-note">電子付款證明暫不可用</p> : null}
      {paymentMode === 'electronic-proof' && paymentProofUpload ? <label className="customer-proof-input">
        <span>付款截圖</span>
        <input
          aria-label="付款截圖"
          type="file"
          accept="image/*"
          disabled={submitting}
          onChange={(event) => setProofFile(event.currentTarget.files?.[0] ?? null)}
        />
        <small>{proofFile ? proofFile.name : '請選擇已完成付款嘅截圖'}</small>
      </label> : null}
    </section>
    {snapshot.combo.ruleLabels.length > 0 ? <div className="customer-combo-save" aria-label="套餐優惠"><strong>已自動套用套餐</strong><span>{snapshot.combo.ruleLabels.join('＋')}</span>{snapshot.combo.savingLabel ? <span>已慳 {snapshot.combo.savingLabel}</span> : null}</div> : null}
  </div>;

  const proofRecovery = lastElectronicSubmissionId ? <section className="customer-proof-recovery" aria-label="付款證明狀態">
    <div className="customer-proof-recovery__header">
      <div><strong>付款證明狀態</strong><small>{lastElectronicSubmissionId}</small></div>
      <span>{customerProofStatusLabel(proofStatus)}</span>
    </div>
    <UiButton variant="secondary" onPress={refreshProofStatus}>更新證明狀態</UiButton>
    {proofStatus === 'resubmission_required' ? <div className="customer-proof-recovery__resubmit">
      <p>付款截圖未能清楚核對，請重新提交一張清晰截圖。</p>
      {paymentProofUpload ? <label className="customer-proof-input">
        <span>重新提交付款截圖</span>
        <input
          aria-label="重新提交付款截圖"
          type="file"
          accept="image/*"
          disabled={resubmitting}
          onChange={(event) => setResubmitFile(event.currentTarget.files?.[0] ?? null)}
        />
        <small>{resubmitFile ? resubmitFile.name : '選擇新截圖'}</small>
      </label> : <p className="customer-payment-note">電子付款證明上載服務暫不可用</p>}
      <UiButton variant="primary" isDisabled={!paymentProofUpload || !resubmitFile || resubmitting} onPress={resubmitPaymentProof}>
        {resubmitting ? '重新上載中…' : '重新上載付款證明'}
      </UiButton>
    </div> : null}
  </section> : null;

  return <main className="customer-app">
    <MobileOrderingShell
      header={<OrderingHeader eyebrow="磨飯 · More Fun" title="自取點餐" subtitle={`餐牌 ${snapshot.catalogRevision}`} />}
      feedback={<div className="customer-feedback-stack"><MutationFeedback tone="info" message={feedback} />{proofRecovery}</div>}
      categories={<CategoryRail items={snapshot.categories} selectedId={active} onSelect={setCategory} emptyLabel="暫時未有餐牌" />}
      products={<ProductList items={productItems} onDirectAction={(id) => port.directAdd(id)} configureContent={(product) => {const config = port.configurationFor(product.id); return config ? <ConfigForm config={config} port={port} /> : <MutationFeedback tone="critical" message="配置不可用" />;}} />}
      accessory={<CartAccessory countLabel={snapshot.countLabel} totalLabel={snapshot.totalLabel} actionLabel="查看購物車" isDisabled={snapshot.lines.length === 0} disabledReason="購物車未有餐點">
        <CartSheet items={cartItems} totalLabel={snapshot.totalLabel} statusLabel={snapshot.quoteStatus === 'quoted' ? '即時權威報價' : '等待報價'} statusTone={snapshot.quoteStatus === 'quoted' ? 'positive' : 'warning'}
          primaryActionLabel={primaryActionLabel} primaryActionDisabled={checkoutDisabled}
          onDecrease={(id) => port.changeQuantity(id, -1)} onIncrease={(id) => port.changeQuantity(id, 1)} onRemove={(id) => {const line = snapshot.lines.find((item) => item.id === id); if (line) port.changeQuantity(id, -line.quantity);}} onPrimaryAction={submit}
          footer={checkoutFooter} />
      </CartAccessory>}
    />
  </main>;
}
